import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../hooks/useTheme";
import useMediaQuery from "../hooks/useMediaQuery";
import useScrollShadow from "../hooks/useScrollShadow";
import useActiveSection from "../hooks/useActiveSection";
import sunIcon from "../assets/images/icons/sun.png";
import moonIcon from "../assets/images/icons/crescent-moon.png";
import "bootstrap/dist/css/bootstrap.min.css";

const SECTIONS = ["home", "about", "projects", "contact"];

const Navigation = ({ variant = "home" }) => {
  const [showLinks, setShowLinks] = useState(false);
  const { isDarkTheme, toggleTheme } = useTheme();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const shadow = useScrollShadow();
  const { t, i18n } = useTranslation();
  const sectionIds = useMemo(() => SECTIONS, []);
  const activeSection = useActiveSection(variant === "home" ? sectionIds : []);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language.startsWith("fr") ? "en" : "fr");
  };

  const closeMenu = () => {
    setShowLinks(false);
  };

  const handleToggleLinks = () => {
    setShowLinks(!showLinks);
  };

  const isProject = variant === "project";

  const navLinks = isProject ? (
    <>
      <Link to="/#home" className="nav-link" onClick={closeMenu}>
        {t("home")}
      </Link>
      <Link to="/#about" className="nav-link" onClick={closeMenu}>
        {t("about")}
      </Link>
      <Link to="/#projects" className="nav-link" onClick={closeMenu}>
        {t("projects")}
      </Link>
      <Link to="/#contact" className="nav-link" onClick={closeMenu}>
        {t("contact")}
      </Link>
      <a
        href={"/cv.pdf"}
        target="_blank"
        rel="noopener noreferrer"
        className="nav-link"
        onClick={closeMenu}
      >
        {t("resume")}
      </a>
    </>
  ) : (
    <>
      {SECTIONS.map((section) => (
        <a
          key={section}
          href={`#${section}`}
          className={`nav-link ${activeSection === section ? "nav-link--active" : ""}`}
          onClick={closeMenu}
        >
          {t(section)}
        </a>
      ))}
    </>
  );

  const logoContent = isProject ? (
    <Link to="/#home" className="nav-link">
      Larzul Julien
    </Link>
  ) : (
    <a href="#home" className="nav-link">
      Larzul Julien
    </a>
  );

  const controls = (
    <div className="nav-controls">
      <button
        type="button"
        className="theme-toggle-btn"
        onClick={toggleTheme}
      >
        <div className="theme-icon">
          <img
            src={sunIcon}
            alt="Light mode"
            className={`icon-sun ${isDarkTheme ? "icon-hidden" : ""}`}
          />
          <img
            src={moonIcon}
            alt="Dark mode"
            className={`icon-moon ${!isDarkTheme ? "icon-hidden" : ""}`}
          />
        </div>
      </button>
      <button
        type="button"
        className="lang-toggle"
        onClick={toggleLanguage}
        aria-label="Toggle language"
      >
        <span className={`lang-toggle__option${i18n.language.startsWith("fr") ? " lang-toggle__option--active" : ""}`}>FR</span>
        <span className={`lang-toggle__option${i18n.language.startsWith("en") ? " lang-toggle__option--active" : ""}`}>EN</span>
      </button>
    </div>
  );

  return (
    <nav
      className={`navigation ${shadow ? "shadow" : ""} ${
        showLinks ? "show-nav" : "hide-nav"
      } ${isMobile ? "isMobile" : ""}`}
    >
      <div className="navigation-logo">{logoContent}</div>
      <div className={`navigation-links ${showLinks ? "show" : ""}`}>
        {navLinks}
        {!isMobile && <span className="nav-link-divider" />}
        {controls}
      </div>
      {isMobile && (
        <button className="navbarBurger" onClick={handleToggleLinks}>
          <span className="burgerBar"></span>
        </button>
      )}
    </nav>
  );
};

export default Navigation;
